import { z } from "zod";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTableHook, tableFeatures } from "@tanstack/react-table";
import { Button } from "@astryxdesign/core/Button";
import { TextInput } from "@astryxdesign/core/TextInput";
import * as stylex from "@stylexjs/stylex";
import { errorMessage, queries } from "../lib/api";
import {
  cloudAPI,
  cloudQuery,
  connectionFormSchema,
  onboardingFormSchema,
  type CloudState,
  type CloudNode,
  type CloudConnection,
  type ConnectionForm,
  type OnboardingForm,
} from "../lib/cloud";
import {
  Drawer,
  PageHeader,
  SectionTabs,
  Stat,
  SearchField,
  Pagination,
} from "../components/Workspace";
import { styles } from "../lib/styles";

export const Route = createFileRoute("/cloud")({
  validateSearch: z.object({
    view: z.enum(["inventory", "connections", "jobs"]).catch("inventory"),
  }),
  component: CloudPage,
});
const date = (value: string | null) => (value ? new Date(value).toLocaleString() : "Never");
const label = (value: string) => value.replaceAll("_", " ");
const { useAppTable, createAppColumnHelper } = createTableHook({ features: tableFeatures({}) });
const helper = createAppColumnHelper<CloudNode>();

function CloudPage() {
  const me = useQuery(queries.me());
  const cloud = useQuery({ ...cloudQuery(), enabled: me.data?.admin === true });
  const { view: tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const setTab = (view: "inventory" | "connections" | "jobs") => {
    void navigate({ search: { view } });
  };
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<CloudNode | null>(null);
  const [connection, setConnection] = useState<CloudConnection | null>(null);
  if (me.isPending) return <p aria-busy="true">Checking cloud permissions…</p>;
  if (!me.data?.admin)
    return (
      <section data-workspace-panel {...stylex.props(styles.panel)}>
        <h1 {...stylex.props(styles.title)}>Cloud nodes</h1>
        <p>Cloud management requires administrator access.</p>
      </section>
    );
  return (
    <>
      <PageHeader
        title="Cloud"
        description="Discover virtual machines and bring selected nodes into managed SSH access."
        action={
          <Button
            label="Add connection"
            variant="primary"
            onClick={() => {
              setConnection(null);
              setEditing(true);
            }}
          />
        }
      />
      {cloud.error && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(cloud.error)}
        </p>
      )}
      {cloud.isPending && <p aria-busy="true">Loading cloud inventory…</p>}
      {cloud.data && (
        <>
          {!cloud.data.configured && (
            <div {...stylex.props(styles.notice)}>
              <p>
                Cloud integration is not configured on this server. Configure the service before
                adding credentials.
              </p>
              <details>
                <summary>Operator setup</summary>
                <p>
                  CLOUD_OWNER_SUBJECT is mandatory: set it to your stable SSO subject. Set
                  CLOUD_KEY_FILE to a private encryption-key file. For onboarding, configure
                  CLOUD_WORKER_CIDR, CLOUD_NETBIRD_MANAGEMENT_URL and the existing NetBird,
                  provisioning-key, gateway and SSH CA settings.
                </p>
                <p>
                  Provide a verified NetBird artifact using CLOUD_NETBIRD_ARTIFACT_FILE,
                  CLOUD_NETBIRD_ARTIFACT_SHA256, CLOUD_NETBIRD_ARTIFACT_VERSION and
                  CLOUD_NETBIRD_ARTIFACT_ARCH. Each worker configuration supplies one
                  architecture-specific package; its architecture must match the target host. Enable
                  CLOUD_FIREWALL_PILOT_VALIDATED only after the temporary security-group behavior
                  has passed the operator pilot. Keep all secret values outside the UI and
                  repository.
                </p>
              </details>
            </div>
          )}
          <div className="stat-grid">
            <Stat
              label="Discovered"
              value={cloud.data.nodes.length}
              detail="Virtual machines across connections"
            />
            <Stat
              label="Managed"
              value={
                cloud.data.nodes.filter(
                  (node) => node.server_id && node.management_status !== "disabled",
                ).length
              }
              detail="Enrolled in managed access"
            />
            <Stat
              label="Connections"
              value={cloud.data.connections.length}
              detail="Configured cloud integrations"
            />
            <Stat
              label="Active jobs"
              value={
                cloud.data.jobs.filter((job) =>
                  ["queued", "working", "cleanup_required"].includes(job.status),
                ).length
              }
              detail="Onboarding and cleanup work"
            />
          </div>
          <SectionTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "inventory", label: "Inventory", count: cloud.data.nodes.length },
              { id: "connections", label: "Connections", count: cloud.data.connections.length },
              { id: "jobs", label: "Jobs", count: cloud.data.jobs.length },
            ]}
          />
          {tab === "connections" && (
            <Connections
              state={cloud.data}
              onEdit={(value) => {
                setConnection(value);
                setEditing(true);
              }}
            />
          )}
          {editing && (
            <Drawer
              title={connection ? "Edit connection" : "Add cloud connection"}
              onClose={() => setEditing(false)}
            >
              <ConnectionEditor
                key={connection?.id ?? "new"}
                connection={connection}
                configured={cloud.data.configured}
                onDone={() => {
                  setEditing(false);
                  setConnection(null);
                }}
              />
            </Drawer>
          )}
          {tab === "inventory" && (
            <>
              <Inventory
                state={cloud.data}
                onSelect={setSelected}
                owner={me.data.subject === cloud.data.owner_subject}
              />
              <p {...stylex.props(styles.small)}>
                Onboarding is manual. Ubuntu/Debian with systemd is supported; other systems remain
                visible and unmanaged.
              </p>
            </>
          )}
          {selected && (
            <Drawer
              title="Review node onboarding"
              description="Review access and trust settings before onboarding this VM."
              onClose={() => setSelected(null)}
            >
              <Onboarding
                key={selected.id}
                node={selected}
                connection={cloud.data.connections.find(
                  (item) => item.id === selected.connection_id,
                )}
                onClose={() => setSelected(null)}
              />
            </Drawer>
          )}
          {tab === "jobs" && <Jobs state={cloud.data} />}
        </>
      )}
    </>
  );
}

function Connections({
  state,
  onEdit,
}: {
  state: CloudState;
  onEdit: (value: CloudConnection) => void;
}) {
  const cache = useQueryClient();
  const sync = useMutation({
    mutationFn: cloudAPI.sync,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>E2E connections</h2>
      {state.connections.length === 0 && (
        <p {...stylex.props(styles.empty)}>Add a connection to discover your VMs.</p>
      )}
      {sync.error && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(sync.error)}
        </p>
      )}
      {sync.isSuccess && <output>Inventory synchronization queued.</output>}
      {state.connections.map((item) => (
        <div key={item.id} className="list-row">
          <div>
            <strong>{item.name}</strong>
            <p {...stylex.props(styles.small)}>
              Project {item.project_id} · {item.location} · {item.enabled ? "Enabled" : "Disabled"}{" "}
              · Credentials {item.credential_set ? "configured" : "missing"}
            </p>
            <p {...stylex.props(styles.small)}>Last synchronized: {date(item.last_sync)}</p>
            {item.last_error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {item.last_error}
              </p>
            )}
          </div>
          <div {...stylex.props(styles.nav)}>
            <Button label="Edit connection" size="sm" onClick={() => onEdit(item)} />
            <Button
              label="Synchronize inventory"
              size="sm"
              isDisabled={!item.enabled || !item.credential_set}
              isLoading={sync.isPending && sync.variables === item.id}
              onClick={() => sync.mutate(item.id)}
            />
          </div>
        </div>
      ))}
    </section>
  );
}

function ConnectionEditor({
  connection,
  configured,
  onDone,
}: {
  connection: CloudConnection | null;
  configured: boolean;
  onDone: () => void;
}) {
  const cache = useQueryClient();
  const [credentialError, setCredentialError] = useState("");
  const save = useMutation({
    mutationFn: cloudAPI.saveConnection,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const defaults: ConnectionForm = {
    id: connection?.id ?? "",
    name: connection?.name ?? "",
    project_id: connection ? String(connection.project_id) : "",
    location: connection?.location === "Chennai" ? "Chennai" : "Delhi",
    enabled: connection?.enabled ?? true,
    network_changes: connection?.network_changes ?? true,
    api_key: "",
    api_token: "",
  };
  const form = useForm({
    defaultValues: defaults,
    validators: { onSubmit: connectionFormSchema },
    onSubmit: async ({ value }) => {
      if (
        (!connection && (!value.api_key || !value.api_token)) ||
        Boolean(value.api_key) !== Boolean(value.api_token)
      ) {
        setCredentialError("Provide both the API key and bearer token together.");
        return;
      }
      setCredentialError("");
      await save.mutateAsync(value);
      form.reset({ ...value, api_key: "", api_token: "" });
      onDone();
    },
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>
        {connection ? "Edit E2E connection" : "Add E2E connection"}
      </h2>
      <p {...stylex.props(styles.small)}>
        Credentials stay on the server. Leave both credential fields empty when editing to retain
        them. Scope the E2E account credentials to the selected project and required capabilities.
      </p>
      <form
        {...stylex.props(styles.formContainer)}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div {...stylex.props(styles.form)}>
          {(
            [
              ["name", "Connection name"],
              ["project_id", "E2E project ID"],
              ["api_key", "API key"],
              ["api_token", "Bearer token"],
            ] as const
          ).map(([name, title]) => (
            <form.Field key={name} name={name}>
              {(field) => (
                <TextInput
                  controlXstyle={styles.inputControl}
                  label={title}
                  type={name.startsWith("api_") ? "password" : "text"}
                  value={field.state.value}
                  onChange={field.handleChange}
                  isRequired={!connection || !name.startsWith("api_")}
                  status={
                    field.state.meta.errors.length
                      ? { type: "error", message: "Check this value" }
                      : undefined
                  }
                />
              )}
            </form.Field>
          ))}
          <form.Field name="location">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                Location
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) =>
                    field.handleChange(event.target.value === "Chennai" ? "Chennai" : "Delhi")
                  }
                >
                  <option>Delhi</option>
                  <option>Chennai</option>
                </select>
              </label>
            )}
          </form.Field>
          <form.Field name="enabled">
            {(field) => (
              <label>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Enable this connection
              </label>
            )}
          </form.Field>
          <form.Field name="network_changes">
            {(field) => (
              <label {...stylex.props(styles.full)}>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Allow temporary SSH network access restricted to the provisioning worker IP
              </label>
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(state) => state.errors.length > 0}>
          {(invalid) =>
            invalid && <p role="alert">Check the connection name, project ID and credentials.</p>
          }
        </form.Subscribe>
        {(credentialError || save.error) && (
          <p role="alert" {...stylex.props(styles.error)}>
            {credentialError || errorMessage(save.error)}
          </p>
        )}
        {save.isSuccess && (
          <output>Connection saved. Synchronize inventory to discover nodes.</output>
        )}
        <div {...stylex.props(styles.actions)}>
          <Button
            label="Save connection"
            variant="primary"
            type="submit"
            isDisabled={!configured}
            isLoading={save.isPending}
          />
          {connection && <Button label="Cancel editing" onClick={onDone} />}
        </div>
      </form>
    </section>
  );
}

function Inventory({
  state,
  onSelect,
  owner,
}: {
  state: CloudState;
  onSelect: (value: CloudNode) => void;
  owner: boolean;
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const cache = useQueryClient();
  const changed = async () => {
    await cache.invalidateQueries({ queryKey: ["cloud"] });
    await cache.invalidateQueries({ queryKey: ["access"] });
  };
  const offboard = useMutation({ mutationFn: cloudAPI.offboard, onSuccess: changed });
  const enable = useMutation({ mutationFn: cloudAPI.enable, onSuccess: changed });
  const columns = useMemo(
    () => [
      helper.display({
        id: "node",
        header: "Virtual machine",
        cell: ({ row }) => (
          <>
            <strong>{row.original.name}</strong>
            <p {...stylex.props(styles.small)}>
              {row.original.provider_id} ·{" "}
              {state.connections.find((item) => item.id === row.original.connection_id)?.name}
            </p>
          </>
        ),
      }),
      helper.display({
        id: "os",
        header: "OS / power",
        cell: ({ row }) => (
          <>
            {row.original.os || "Unknown OS"}
            <p {...stylex.props(styles.small)}>
              {row.original.missing ? "Missing from inventory" : row.original.status}
            </p>
          </>
        ),
      }),
      helper.display({
        id: "address",
        header: "Addresses",
        cell: ({ row }) => (
          <>
            {row.original.public_ip || "No public IP"}
            <p {...stylex.props(styles.small)}>{row.original.private_ip}</p>
          </>
        ),
      }),
      helper.display({
        id: "management",
        header: "Management",
        cell: ({ row }) => (
          <>
            <span {...stylex.props(styles.status)}>
              {label(row.original.management_status || "discovered")}
            </span>
            <p {...stylex.props(styles.small)}>Seen {date(row.original.last_seen)}</p>
          </>
        ),
      }),
      helper.display({
        id: "actions",
        header: "Manage",
        cell: ({ row }) => {
          const node = row.original;
          const busy = state.jobs.some(
            (job) =>
              job.node_id === node.id &&
              ["queued", "working", "cleanup_required"].includes(job.status),
          );
          const connection = state.connections.find((item) => item.id === node.connection_id);
          if (node.server_id && node.management_status === "disabled") {
            return owner ? (
              <Button
                label="Enable managed access"
                size="sm"
                isDisabled={busy || node.missing || !connection?.enabled || !state.configured}
                isLoading={enable.isPending && enable.variables === node.id}
                onClick={() => enable.mutate(node.id)}
              />
            ) : (
              <span {...stylex.props(styles.small)}>Owner can enable access</span>
            );
          }
          return node.server_id ? (
            <Button
              label="Disable managed access"
              size="sm"
              isLoading={offboard.isPending && offboard.variables === node.id}
              onClick={() => offboard.mutate(node.id)}
            />
          ) : (
            <Button
              label="Review onboarding"
              size="sm"
              isDisabled={node.missing || busy || !connection?.enabled || !state.configured}
              onClick={() => onSelect(node)}
            />
          );
        },
      }),
    ],
    [state, onSelect, offboard, enable, owner],
  );
  const needle = search.trim().toLowerCase();
  const data = state.nodes.filter(
    (node) =>
      (filter === "all" || (filter === "managed" ? !!node.server_id : !node.server_id)) &&
      [
        node.name,
        node.provider_id,
        node.os,
        node.public_ip,
        node.private_ip,
        node.management_status,
      ].some((value) => value.toLowerCase().includes(needle)),
  );
  const currentPage = Math.min(page, Math.max(0, Math.ceil(data.length / 15) - 1));
  const table = useAppTable({
    columns,
    data: data.slice(currentPage * 15, (currentPage + 1) * 15),
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Discovered nodes</h2>
      <div className="table-toolbar">
        <SearchField
          label="Find a node"
          placeholder="Search name, OS or address…"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(0);
          }}
        />
        <label {...stylex.props(styles.field)}>
          Management
          <select
            {...stylex.props(styles.select)}
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value);
              setPage(0);
            }}
          >
            <option value="all">All nodes</option>
            <option value="managed">Managed</option>
            <option value="unmanaged">Unmanaged</option>
          </select>
        </label>
      </div>
      {(offboard.error || enable.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(offboard.error || enable.error)}
        </p>
      )}
      {enable.isSuccess && (
        <output>
          Managed access enable requested. Access synchronization verifies the server before
          allowing connections.
        </output>
      )}
      {offboard.isSuccess && (
        <output>Managed access disable requested. Existing recovery access is preserved.</output>
      )}
      {data.length === 0 ? (
        <p {...stylex.props(styles.empty)}>
          {state.nodes.length
            ? "No matching nodes."
            : "No nodes discovered. Synchronize an enabled E2E connection."}
        </p>
      ) : (
        <div {...stylex.props(styles.tableWrap)}>
          <table {...stylex.props(styles.table)}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id} {...stylex.props(styles.th)}>
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} {...stylex.props(styles.td)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={currentPage} total={data.length} onChange={setPage} />
    </section>
  );
}

function Onboarding({
  node,
  connection,
  onClose,
}: {
  node: CloudNode;
  connection: CloudConnection | undefined;
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const onboard = useMutation({
    mutationFn: cloudAPI.onboard,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const defaults: OnboardingForm = {
    alias: "",
    login: "",
    bootstrap_user: "ubuntu",
    bootstrap_address: node.public_ip || node.private_ip,
    port: "22",
    host_key: "",
    trust_mode: "tofu",
    install_mode: "host",
    temporary_network: false,
  };
  const form = useForm({
    defaultValues: defaults,
    validators: { onSubmit: onboardingFormSchema },
    onSubmit: async ({ value }) => {
      await onboard.mutateAsync({ id: node.id, value });
      onClose();
    },
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Onboard {node.name}</h2>
      <p {...stylex.props(styles.description)}>
        The worker verifies the host, detects its OS and existing configuration, and selects a
        supported adapter. An incompatible NetBird installation remains unchanged and unmanaged.
      </p>
      <p {...stylex.props(styles.notice)}>
        You authorize first-connection trust for this selected VM, or you can provide a verified
        host key below. Existing SSH recovery access, firewall rules and unrelated NetBird settings
        are preserved. Temporary bootstrap credentials and network access are removed after
        verification.
      </p>
      <form
        {...stylex.props(styles.formContainer)}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div {...stylex.props(styles.form)}>
          {(
            [
              ["alias", "Managed SSH alias"],
              ["login", "Managed login account"],
              ["bootstrap_user", "Existing administrator account"],
              ["bootstrap_address", "Bootstrap IP address"],
              ["port", "SSH port"],
            ] as const
          ).map(([name, title]) => (
            <form.Field key={name} name={name}>
              {(field) => (
                <TextInput
                  controlXstyle={styles.inputControl}
                  label={title}
                  value={field.state.value}
                  onChange={field.handleChange}
                  isRequired
                  status={
                    field.state.meta.errors.length
                      ? { type: "error", message: "Check this value" }
                      : undefined
                  }
                />
              )}
            </form.Field>
          ))}
          <form.Field name="trust_mode">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                SSH host identity
                <select
                  aria-label="SSH host identity"
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) =>
                    field.handleChange(event.target.value === "manual" ? "manual" : "tofu")
                  }
                >
                  <option value="tofu">Trust selected VM on first connection</option>
                  <option value="manual">Verify with a host public key</option>
                </select>
              </label>
            )}
          </form.Field>
          <form.Subscribe selector={(state) => state.values.trust_mode}>
            {(mode) =>
              mode === "manual" ? (
                <form.Field name="host_key">
                  {(field) => (
                    <TextInput
                      label="Trusted SSH host public key"
                      controlXstyle={styles.inputControl}
                      value={field.state.value}
                      onChange={field.handleChange}
                      isRequired
                      status={
                        field.state.meta.errors.length
                          ? { type: "error", message: "Enter a valid SSH host public key" }
                          : undefined
                      }
                    />
                  )}
                </form.Field>
              ) : (
                <p {...stylex.props(styles.small)}>
                  The selected VM’s identity is trusted on its first connection. Its SSH host key is
                  saved automatically; later key changes block access.
                </p>
              )
            }
          </form.Subscribe>
          <form.Field name="install_mode">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                NetBird installation
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) =>
                    field.handleChange(event.target.value === "docker" ? "docker" : "host")
                  }
                >
                  <option value="host">Directly on host (recommended)</option>
                  <option value="docker" disabled>
                    Docker (adapter validation pending)
                  </option>
                </select>
              </label>
            )}
          </form.Field>
          <form.Field name="temporary_network">
            {(field) => (
              <label>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  disabled={!connection?.network_changes}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Allow a temporary worker-IP-only SSH rule
                {!connection?.network_changes && " (disabled for this connection)"}
              </label>
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(state) => state.errors.length > 0}>
          {(invalid) =>
            invalid && (
              <p role="alert">
                Check the alias, accounts, IP address, port and host key before onboarding.
              </p>
            )
          }
        </form.Subscribe>
        {onboard.error && (
          <p role="alert" {...stylex.props(styles.error)}>
            {errorMessage(onboard.error)}
          </p>
        )}
        <div {...stylex.props(styles.actions)}>
          <Button
            variant="primary"
            label="Start onboarding"
            type="submit"
            isLoading={onboard.isPending}
          />
          <Button label="Cancel" onClick={onClose} />
        </div>
      </form>
    </section>
  );
}

function Jobs({ state }: { state: CloudState }) {
  const cache = useQueryClient();
  const cancel = useMutation({
    mutationFn: cloudAPI.cancel,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const retry = useMutation({
    mutationFn: cloudAPI.retry,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Onboarding and cleanup</h2>
      {(retry.error || cancel.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(retry.error || cancel.error)}
        </p>
      )}
      {cancel.isSuccess && (
        <output>Cancellation requested. Temporary bootstrap access will be cleaned up.</output>
      )}
      {state.jobs.length === 0 && <p {...stylex.props(styles.empty)}>No onboarding jobs yet.</p>}
      {state.jobs.map((job) => (
        <div key={job.id} {...stylex.props(styles.pageHeading)}>
          <div>
            <strong>
              {state.nodes.find((node) => node.id === job.node_id)?.name ?? job.node_id}
            </strong>
            <p>
              <span {...stylex.props(styles.status)}>{label(job.status)}</span> {label(job.stage)}
            </p>
            <p {...stylex.props(styles.small)}>Updated {date(job.updated_at)}</p>
            {job.error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {job.error}
              </p>
            )}
          </div>
          {["queued", "working"].includes(job.status) && (
            <Button
              label="Cancel onboarding"
              size="sm"
              isLoading={cancel.isPending && cancel.variables === job.id}
              onClick={() => cancel.mutate(job.id)}
            />
          )}
          {job.status === "cleanup_required" && (
            <Button
              label="Retry cleanup"
              size="sm"
              isLoading={retry.isPending && retry.variables === job.id}
              onClick={() => retry.mutate(job.id)}
            />
          )}
        </div>
      ))}
    </section>
  );
}
